"use client";

import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { shouldIgnoreShortcutTarget } from "./should-ignore-shortcut-target";

export const OPEN_SHORTCUT_HELP_EVENT = "upsider:open-shortcut-help";

export function openShortcutHelp() {
  window.dispatchEvent(new CustomEvent(OPEN_SHORTCUT_HELP_EVENT));
}

type Shortcut = {
  keys: string[];
  label: string;
};

type ShortcutGroup = {
  heading: string;
  shortcuts: Shortcut[];
};

const SHORTCUT_GROUPS: ShortcutGroup[] = [
  {
    heading: "Global",
    shortcuts: [
      { keys: ["⌘K", "Ctrl+K"], label: "Command palette" },
      { keys: ["?"], label: "Keyboard shortcuts" },
    ],
  },
  {
    heading: "Optimizer",
    shortcuts: [
      { keys: ["r"], label: "Refresh" },
      { keys: ["/"], label: "Focus player search" },
      { keys: ["Esc"], label: "Close share / filters" },
    ],
  },
  {
    heading: "Line history",
    shortcuts: [
      { keys: ["←", "→"], label: "Scrub" },
      { keys: ["Esc"], label: "Clear hover" },
    ],
  },
  {
    heading: "Odds screen",
    shortcuts: [
      { keys: ["/"], label: "Search" },
      { keys: ["H"], label: "Hide the focused row" },
    ],
  },
  {
    heading: "Odds screen, wide layout",
    shortcuts: [
      { keys: ["M"], label: "Markets menu" },
      { keys: ["D"], label: "Hide or show DFS & pick'em" },
      { keys: ["P"], label: "Hide or show prediction markets" },
      { keys: ["S"], label: "Hide or show sweepstakes" },
      { keys: ["O"], label: "Hide or show offshore books" },
    ],
  },
];

function ShortcutKeys({ keys }: { keys: string[] }) {
  return (
    <span className="flex items-center gap-1">
      {keys.map((key, index) => (
        <span key={key} className="flex items-center gap-1">
          {index > 0 ? (
            <span className="text-[10px] text-muted-foreground/50">/</span>
          ) : null}
          <kbd className="inline-flex min-w-[1.5rem] items-center justify-center rounded border border-white/[0.08] bg-white/[0.05] px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
            {key}
          </kbd>
        </span>
      ))}
    </span>
  );
}

export function ShortcutHelp() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      if (event.repeat) return;
      if (event.key !== "?") return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (shouldIgnoreShortcutTarget(event.target)) return;

      event.preventDefault();
      setOpen(true);
    };

    const handleExternalOpen = () => setOpen(true);
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener(OPEN_SHORTCUT_HELP_EVENT, handleExternalOpen);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener(OPEN_SHORTCUT_HELP_EVENT, handleExternalOpen);
    };
  }, []);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="w-[min(28rem,calc(100vw-2rem))]"
        contentClassName="gap-0 overflow-hidden p-0 text-foreground"
        closeButtonClassName="right-1.5 top-1.5 text-muted-foreground hover:text-foreground"
        aria-describedby={undefined}
        onKeyDown={(event) => {
          event.stopPropagation();
        }}
      >
        <div className="border-b border-border/60 px-6 pb-4 pt-5 pr-14">
          <DialogTitle className="font-display text-lg font-bold tracking-tight text-foreground">
            Keyboard shortcuts
          </DialogTitle>
        </div>

        <div className="max-h-[min(70vh,30rem)] space-y-5 overflow-y-auto px-6 py-5 scrollbar-custom scrollbar-color-primary">
          {SHORTCUT_GROUPS.map((group) => (
            <section key={group.heading} className="space-y-1.5">
              <h3 className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground/70">
                {group.heading}
              </h3>
              <ul className="space-y-1">
                {group.shortcuts.map((shortcut) => (
                  <li
                    key={shortcut.label}
                    className="flex items-center justify-between gap-4 py-1"
                  >
                    <span className="text-sm text-foreground/90">{shortcut.label}</span>
                    <ShortcutKeys keys={shortcut.keys} />
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
